/**
 * The shop's own product choices — WooCommerce's variation selects, the
 * quantity box and the price line — offered inside the designer's Options
 * step, the way Zazzle puts size, style and colour in its editor.
 *
 * Nothing is copied into our state. The buttons drive WooCommerce's own
 * controls (set the select, fire the change its scripts listen for), so the
 * variation id, the price, the stock message and our own variation-conditioned
 * questions all follow exactly as if the shopper had used the page.
 */

export interface PageChoice {
  value: string;
  label: string;
  disabled: boolean;
}

export interface PageAttribute {
  name: string;
  label: string;
  value: string;
  choices: PageChoice[];
}

/** The part of a `<select>` this module reads; lets the model be tested without a DOM. */
export interface SelectLike {
  name: string;
  id?: string;
  value: string;
  options: ArrayLike<{ value: string; text: string; disabled: boolean; className?: string }>;
}

/** "attribute_pa_boja-majice" → "Boja majice" when the page has no label for it. */
export function attributeFallbackLabel(name: string): string {
  const bare = name.replace(/^attribute_/, '').replace(/^pa_/, '').replace(/[-_]+/g, ' ').trim();
  return bare ? bare.charAt(0).toLocaleUpperCase('sr-RS') + bare.slice(1) : name;
}

export function readPageAttributes(
  selects: readonly SelectLike[],
  labelFor: (select: SelectLike) => string | undefined,
): PageAttribute[] {
  return selects.map((select) => ({
    name: select.name,
    label: (labelFor(select) ?? '').trim() || attributeFallbackLabel(select.name),
    value: select.value,
    choices: Array.from(select.options)
      // The empty "Izaberite opciju" row is WooCommerce's prompt, not a choice.
      .filter((option) => option.value !== '')
      .map((option) => ({
        value: option.value,
        label: option.text.trim() || option.value,
        // Woo greys out combinations that do not exist with `disabled` or,
        // in its attribute-filtering script, with an `enabled` class missing.
        disabled: option.disabled,
      })),
  }));
}

function variationForm(root: ParentNode = document): HTMLFormElement | null {
  return root.querySelector<HTMLFormElement>('form.variations_form');
}

function variationSelects(root: ParentNode = document): HTMLSelectElement[] {
  const form = variationForm(root);
  return form ? [...form.querySelectorAll<HTMLSelectElement>('select[name^="attribute_"]')] : [];
}

/** Whether the page offers anything for the Options step. */
export function pageOptionsAvailable(root: ParentNode = document): boolean {
  return variationSelects(root).length > 0;
}

/**
 * Picks a value in WooCommerce's own select. Its scripts are jQuery handlers,
 * which a native `change` reaches; jQuery's own `trigger` is used as well when
 * the page has jQuery, because some themes bind with `.on('change', …)` on a
 * wrapper that only a jQuery-triggered event bubbles through reliably.
 */
function pick(select: HTMLSelectElement, value: string): void {
  select.value = value;
  select.dispatchEvent(new Event('change', { bubbles: true }));
  const jq = (window as unknown as { jQuery?: (el: Element) => { trigger(name: string): void } }).jQuery;
  try {
    jq?.(select).trigger('change');
  } catch {
    /* the native event already went */
  }
}

function priceText(): string {
  const variation = document.querySelector<HTMLElement>('.single_variation_wrap .woocommerce-variation-price .price');
  const text = (variation?.innerText || variation?.textContent || '').trim();
  if (text) return text;
  const base = document.querySelector<HTMLElement>('.summary .price, .product .price');
  return (base?.innerText || base?.textContent || '').trim();
}

export const PAGE_OPTION_TEXTS = {
  quantity: 'Količina',
  less: 'Manje',
  more: 'Više',
  price: 'Cena',
};

/** Mounts the variation buttons, the quantity stepper and the live price into `host`. */
export function mountPageOptions(host: HTMLElement): () => void {
  const wrap = document.createElement('div');
  wrap.className = 'pz-page-options';
  host.append(wrap);

  const labelFor = (select: SelectLike): string | undefined => {
    const id = select.id;
    if (!id) return undefined;
    // CSS.escape is missing in some old WebViews; a quoted value only needs " and \ escaped.
    const css = (globalThis as { CSS?: { escape?: (v: string) => string } }).CSS;
    const safe = typeof css?.escape === 'function' ? css.escape(id) : id.replace(/["\\]/g, '\\$&');
    const label = document.querySelector<HTMLLabelElement>(`label[for="${safe}"]`);
    return label?.textContent ?? undefined;
  };

  let signature = '';
  const paint = (): void => {
    const selects = variationSelects();
    const attributes = readPageAttributes(selects, labelFor);
    const quantity = document.querySelector<HTMLInputElement>('form.cart input.qty');
    const price = priceText();
    const next = JSON.stringify([attributes, quantity?.value ?? '', quantity?.disabled ?? true, price]);
    if (next === signature) return;
    signature = next;
    wrap.textContent = '';

    for (const [index, attribute] of attributes.entries()) {
      const group = document.createElement('div');
      group.className = 'pz-page-opt';
      group.setAttribute('role', 'radiogroup');
      const title = document.createElement('div');
      title.className = 'pz-page-opt-title';
      const picked = attribute.choices.find((choice) => choice.value === attribute.value);
      title.textContent = picked ? `${attribute.label}: ${picked.label}` : attribute.label;
      group.setAttribute('aria-label', attribute.label);
      const row = document.createElement('div');
      row.className = 'pz-page-opt-row';
      for (const choice of attribute.choices) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pz-page-opt-btn';
        button.textContent = choice.label;
        button.disabled = choice.disabled;
        const on = choice.value === attribute.value;
        button.classList.toggle('is-active', on);
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', String(on));
        button.addEventListener('click', () => {
          const select = variationSelects()[index];
          if (!select) return;
          pick(select, choice.value);
          // WooCommerce re-filters the other attributes after its own handler.
          window.setTimeout(paint, 60);
        });
        row.append(button);
      }
      group.append(title, row);
      wrap.append(group);
    }

    if (quantity && !quantity.disabled && quantity.type !== 'hidden') {
      const group = document.createElement('div');
      group.className = 'pz-page-opt';
      const title = document.createElement('div');
      title.className = 'pz-page-opt-title';
      title.textContent = PAGE_OPTION_TEXTS.quantity;
      const row = document.createElement('div');
      row.className = 'pz-page-qty';
      const min = Math.max(1, Number(quantity.min) || 1);
      const max = Number(quantity.max) > 0 ? Number(quantity.max) : Infinity;
      const current = Math.max(min, Number(quantity.value) || min);
      const step = (delta: number): void => {
        const value = Math.min(max, Math.max(min, (Number(quantity.value) || min) + delta));
        quantity.value = String(value);
        quantity.dispatchEvent(new Event('input', { bubbles: true }));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
        paint();
      };
      const less = document.createElement('button');
      less.type = 'button';
      less.className = 'pz-page-opt-btn';
      less.textContent = '−';
      less.setAttribute('aria-label', PAGE_OPTION_TEXTS.less);
      less.disabled = current <= min;
      less.addEventListener('click', () => step(-1));
      const count = document.createElement('output');
      count.className = 'pz-page-qty-n';
      count.textContent = String(current);
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'pz-page-opt-btn';
      more.textContent = '+';
      more.setAttribute('aria-label', PAGE_OPTION_TEXTS.more);
      more.disabled = current >= max;
      more.addEventListener('click', () => step(1));
      row.append(less, count, more);
      group.append(title, row);
      wrap.append(group);
    }

    if (price) {
      const line = document.createElement('div');
      line.className = 'pz-page-price';
      line.textContent = `${PAGE_OPTION_TEXTS.price}: ${price}`;
      wrap.append(line);
    }
  };

  paint();
  // WooCommerce changes its selects and its price line through jQuery, which
  // native listeners never hear; a light poll while the step is open keeps the
  // buttons honest whatever the theme does.
  const timer = window.setInterval(paint, 500);
  return () => {
    window.clearInterval(timer);
    wrap.remove();
  };
}
